import type { Placement } from '@popperjs/core';
import type { ButtonSize } from '@timelyapp/react';
import { Text } from '@timelyapp/react';
import ClearableButton from 'components/buttons/ClearableButton';
import DropdownBuilder from 'components/DropdownBuilder';
import Popover from 'components/popovers/Popover';
import { Radio } from 'components/Radio';
import { RadioGroup } from 'components/RadioGroup';
import type { TagOption } from 'factories/tags';
import { buildTagFilterOptions } from 'factories/tags';
import { flatten } from 'lodash-es';
import React, { useCallback, useMemo, useState } from 'react';
import FontAwesome from 'react-fontawesome';
import type { Tag } from 'types/tag';
import { TagFilterMode } from 'types/tag';
import { naturalCompare } from 'utils/naturalSort';
import { applyTagGroupFilter, processTagSelection } from 'utils/tags';
import { countableLabel } from 'utils/text-helper';
import FilterDropDownItem from './FilterDropdownItem';
import FilterGroups from './FilterGroups';
import styles from './TagFilterDropdown.module.scss';

export const TAG_FILTER_DROPDOWN_TEST_ID = 'tag-filter-dropdown';

interface Props {
  tags: Array<Tag>;
  onChange: (ids: Array<number>) => void;
  selected: Array<number>;
  onTagFilterModeChange?: (mode: TagFilterMode) => void;
  tagFilterMode?: TagFilterMode;
  unrestrictedWhenEmpty?: boolean;
  highlightModified?: boolean;
  hideTagsMarkedAsRemoved?: boolean;
  disableSelectNone?: boolean;
  disableSelectAll?: boolean;
  singleChildSelect?: boolean;
  batchMode?: boolean;
  emptyLabel?: string;
  disabled?: boolean;
  onSelectAll?: () => void;
  position?: Placement;
  fixed?: boolean;
  clearTooltip?: string;
  size?: ButtonSize;
  hideIcon?: boolean;
  testId?: string;
  renderTarget?: (isOpen: boolean) => React.ReactElement;
  listFooter?: (close: () => void) => React.ReactNode;
}

interface FilterGroup {
  value: null | 'active' | 'archived';
  name: string;
}

const UNTAGGED: Tag = {
  sequence: -1,
  id: 0,
  name: 'Untagged',
  emoji: 'https://twemoji.maxcdn.com/36x36/1f516.png',
  children: [],
  active: true,
};

const TagFilterDropdown: React.FC<Props> = ({
  tags,
  size,
  onChange,
  selected,
  tagFilterMode,
  onTagFilterModeChange,
  unrestrictedWhenEmpty,
  disableSelectNone,
  disableSelectAll,
  highlightModified = true,
  singleChildSelect = false,
  emptyLabel,
  disabled,
  onSelectAll,
  position = 'bottom',
  fixed,
  batchMode,
  hideTagsMarkedAsRemoved = true,
  clearTooltip,
  hideIcon,
  testId,
  renderTarget,
  listFooter,
}) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [filterGroup, setFilterGroupInState] = useState<null | 'active' | 'archived'>(null);
  const [filterString, onSetFilterString] = useState('');

  const allTags = useMemo(() => {
    const handleRemovedTags = hideTagsMarkedAsRemoved ? tags.filter(t => !t.hasBeenRemoved) : tags;
    if (batchMode) {
      return handleRemovedTags.sort((a, b) => naturalCompare(a.sequence, b.sequence));
    }
    return [UNTAGGED, ...handleRemovedTags].sort((a, b) => naturalCompare(a.sequence, b.sequence));
  }, [tags, batchMode, hideTagsMarkedAsRemoved]);

  const filteredTags = useMemo(
    () => applyTagGroupFilter(allTags, filterGroup),
    [filterGroup, allTags]
  );

  const options = useMemo(
    () => buildTagFilterOptions(filteredTags, selected),
    [filteredTags, selected]
  );

  const hasArchivedTags = useMemo(() => {
    if (allTags.some(p => !p.active)) {
      return true;
    } else {
      return allTags.some(p => (p.children || []).some(p => !p.active));
    }
  }, [allTags]);

  const setFilterGroup = (g: null | 'active' | 'archived') => {
    setFilterGroupInState(g);
  };

  const handleClear = useCallback(
    (e: React.SyntheticEvent) => {
      e.stopPropagation();
      onChange([]);
    },
    [onChange]
  );

  const allTagLeaves = useMemo(() => {
    const allTagsAsOptions = buildTagFilterOptions(allTags, selected);
    return allTagsAsOptions
      .filter(tag => (tag.children?.length ?? 0) === 0) // Root tags without children
      .concat(flatten(allTagsAsOptions.map(tag => tag.children ?? []))); // Children of root tags
  }, [allTags, selected]);

  const numSelected = useMemo(
    () => allTagLeaves.filter(tag => tag.isSelected).length,
    [allTagLeaves]
  );
  const numTags = allTagLeaves.length;

  const emptySelection = numSelected === 0;
  const isEmptyAndUnrestricted = unrestrictedWhenEmpty && emptySelection;
  const showClearButton = (unrestrictedWhenEmpty || clearTooltip) && !emptySelection;

  const isSelected = !emptySelection && highlightModified;

  const filterGroups: null | Array<FilterGroup> = hasArchivedTags
    ? [
        { value: null, name: 'All tags' },
        { value: 'active', name: 'Active' },
        { value: 'archived', name: 'Archived' },
      ]
    : null;

  let prefix = '';
  if (tagFilterMode !== undefined && numSelected > 1) {
    prefix = tagFilterMode === TagFilterMode.OR ? 'Any: ' : 'All: ';
  }
  let label = isEmptyAndUnrestricted
    ? 'Any tag'
    : countableLabel('tag', {
        selected: numSelected,
        total: numTags,
      });

  if (emptySelection && !isEmptyAndUnrestricted) {
    label = emptyLabel! || 'All tags';
  }

  label = prefix + label;

  // Always expand parent IDs to their visible children so the selection only ever contains
  // leaf IDs that are actually visible in the current filter view. This prevents archived
  // children from being implicitly selected when only an active parent is chosen, and
  // ensures "All tags" mode sends archived child IDs explicitly when they are visible.
  const resolveFilteredIds = useCallback(
    (ids: Array<number>): Array<number> => {
      return ids.flatMap(id => {
        const opt = options.find(o => o.id === id);
        if (opt?.children && opt.children.length > 0) {
          return opt.children.map(c => c.id);
        }
        return id;
      });
    },
    [options]
  );

  const handleChange = (optionId: number | null, option: TagOption | null) => {
    if (option == null || optionId == null) {
      return;
    }
    const ids = processTagSelection(option, {
      options,
      selection: selected,
      singleChildSelect,
    });

    onChange(resolveFilteredIds(ids));
  };

  const createHandleBulkAction = (action: 'all' | 'none') => () => {
    if (action === 'none') {
      onChange([]);
    }
    if (action === 'all') {
      if (onSelectAll) {
        // onSelectAll callers manage their own ID format; resolveFilteredIds is not applied here
        onSelectAll();
      } else {
        onChange(resolveFilteredIds(options.map(tag => tag.id)));
      }
    }
  };

  const onlySingleSelect = singleChildSelect;
  const inputActions = (
    <>
      {!disableSelectAll && !onlySingleSelect && (
        <button type="button" onClick={createHandleBulkAction('all')}>
          All
        </button>
      )}
      {!disableSelectNone && (
        <button type="button" onClick={createHandleBulkAction('none')}>
          None
        </button>
      )}
    </>
  );

  const listClass = tagFilterMode ? styles.hasTagFilterMode : undefined;

  return (
    <div data-testid={testId} style={{ display: 'inline-block' }}>
      <Popover
        width={352} // Account for border
        isOpen={isOpen}
        onOpen={() => setIsOpen(true)}
        onClose={() => setIsOpen(false)}
        position={position}
        fixed={fixed}
        target={
          renderTarget ? (
            renderTarget(isOpen)
          ) : (
            <ClearableButton
              data-testid={TAG_FILTER_DROPDOWN_TEST_ID}
              priority="medium"
              selected={isSelected}
              disabled={disabled}
              onClear={showClearButton ? handleClear : undefined}
              isActive={null} // Causes PopoverTarget to control caret
              clearTooltip={clearTooltip || 'Clear the tag filter'}
              size={size}
            >
              {!hideIcon && <FontAwesome name="tag" />}
              <span>{label}</span>
            </ClearableButton>
          )
        }
      >
        {position => (
          <DropdownBuilder
            value={null}
            values={selected}
            options={options}
            onChange={handleChange}
            isOpen={isOpen}
            onSetIsOpen={setIsOpen}
            renderOption={renderOption}
            inputIcon={<FontAwesome name="search" />}
            inputCaret={false}
            placeholder="Search tags"
            inputActions={inputActions}
            disabled={disabled}
            filterString={filterString}
            onSetFilterString={onSetFilterString}
            highlightSelection={false}
            popoverFixed={fixed}
          >
            {context => (
              <div>
                {filterGroups && (
                  <div style={{ padding: '12px 12px 0 12px' }}>
                    <FilterGroups<FilterGroup['value'], FilterGroup>
                      active={filterGroup}
                      groups={filterGroups}
                      onChange={setFilterGroup}
                    />
                  </div>
                )}
                {tagFilterMode && (
                  <div className={styles.tagFilterModeContainer}>
                    <RadioGroup<TagFilterMode>
                      onChange={onTagFilterModeChange}
                      selectedValue={tagFilterMode}
                      className={styles.tagFilterMode}
                    >
                      <Radio
                        label="Match any tag"
                        value={TagFilterMode.OR}
                        className={styles.option}
                        data-testid="tag-filter-mode-or"
                      />
                      <Text.Label size="xsmall" className={styles.description}>
                        Entries that contain any of your selected tags
                      </Text.Label>
                      <Radio
                        label="Match all tags"
                        value={TagFilterMode.AND}
                        className={styles.option}
                        data-testid="tag-filter-mode-and"
                      />
                      <Text.Label size="xsmall" className={styles.description}>
                        Entries that contain all of your selected tags
                      </Text.Label>
                    </RadioGroup>
                  </div>
                )}
                <div style={{ padding: filterGroups ? '6px 12px 12px' : 12 }}>
                  {context.renderInput({ autoFocus: true, disabled })}
                </div>
                {context.renderList({ position, className: listClass })}
                {listFooter?.(() => setIsOpen(false))}
              </div>
            )}
          </DropdownBuilder>
        )}
      </Popover>
    </div>
  );
};

const renderOption = (item: TagOption) => (
  <FilterDropDownItem
    item={item}
    checked={item.isSelected}
    partiallyChecked={item.isPartiallySelected}
    indent={!!item.parent_id}
    resource="tag"
    nonSelectable={item.isSelectable === false}
  />
);
export default TagFilterDropdown;
